import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {UntypedFormGroup} from "@angular/forms";
import {LocalStorageService} from "./local-storage.service";
import {Router} from "@angular/router";
import {UserInterface} from "../interfaces/user.interface";
import {CompanyInterface} from "../interfaces/company.interface";
import {EmployeeModulesEnum} from "../../app/employees/enums/employee-modules.enum";
import {PermissionEnum} from "../../app/employees/enums/permission.enum";
import {Store} from "@ngrx/store";
import {SetHost} from "../store/store.actions";
import {selectHost} from "../store/store.selectors";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
    private token: string;
    private user: UserInterface;
    host: string = '';
    constructor(
      private network: HttpClient,
      private storage: LocalStorageService,
      private router: Router,
      private store: Store
    ) {
        this.store.select(selectHost).subscribe((host) => {
            this.host = host;
        })
    }

    isAWS() {
        return this.host.includes('aws.fodimi.pl');
    }

    getToken(): string {
        return this.token || this.storage.getItem('token');
    }

    isLogged(): boolean {
        return !!this.getToken();
    }

    hasAccess(module: EmployeeModulesEnum, needsWrite: boolean): boolean {
        if (!this.getUser()?.permissions || this.getUser()?.is_admin) {
            return true;
        }

        switch (this.getUser()?.permissions[module]) {
            case PermissionEnum.NONE:
                return false;
            case PermissionEnum.WRITE:
                return true;
            case PermissionEnum.READ:
                return !needsWrite;
        }

        return true;
    }

    getUser(): UserInterface {
      return this.user || this.storage.getItem('user');
    }

    login(form: UntypedFormGroup) {
        return new Promise((resolve, reject) => {
            this.network.post<{ token: string, user: UserInterface }>(`${form.value.host}/api_company/auth/login`, form.value).subscribe((result) => {
                this.store.dispatch(SetHost({host: form.value.host}));
                this.userRegistered(result);
                resolve(result.user);
            }, () => {
                reject(false);
            })
        });
    }

    reset(email: string) {
        return this.network.post('/api_company/auth/reset', {email})
    }

    refreshUserProfile() {
        return new Promise((resolve, reject) => {
            this.network.get<UserInterface>('/api_company/auth/user-profile').subscribe((result) => {
                this.saveUser(result);
                return resolve(true);
            }, () => {
                return reject(false);
            })
        });
    }

    changePasswordViaReset(data) {
        return this.network.post('/api/auth/password', data);
    }

    logout(navigateToLogin = true) {
        this.removeRestaurantSelection();
        this.removeUser();
        this.removeToken();
        if (navigateToLogin) {
            this.router.navigateByUrl('/auth/login');
        }
    }

    changePassword(data) {
        return this.network.post('/api_company/auth/password', data);
    }

    register(data) {
      return this.network.post<any>(`${data.host}/api_company/auth/register`, data);
    }

    userRegistered(data: { token: string, user: UserInterface }) {
        this.saveToken(data.token);
        this.saveUser(data.user);
    }

    updateCompanyInfo(company: CompanyInterface) {
        const user = this.getUser();
        user.company = company;
        this.saveUser(user);
    }

    private removeUser() {
        this.user = null;
        this.storage.removeItem('user');
    }

    private removeToken() {
        this.token = null;
        this.storage.removeItem('token');
    }

    private removeRestaurantSelection() {
        this.storage.removeItem('current_restaurant');
    }

    private saveUser(user: UserInterface) {
        this.user = user;
        this.storage.setItem('user', user);
    }

    private saveToken(token: string) {
        this.token = token;
        this.storage.setItem('token', token);
    }
}
